import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import {
  KpiTile,
  KpiLoadingState,
  LoadingState,
  ErrorState,
  EmptyState,
  HubSection,
  StrengthBar,
  AttentionBadge,
  AskAboutThis,
  beliefSentence,
  directionInfo,
  healthStatus,
  supportTier,
  conflictKind,
  verdict as verdictLabel,
  hitRatePct,
  roundMiss,
  pct,
  formatDay,
} from "@/components/research-hub";
import { useCioOversightPanel } from "@/hooks/useResearchHubCio";

type Row = Record<string, any>;
const HUB = "/partner/research-hub";

export default function CioToday() {
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useCioOversightPanel(12);

  if (isLoading)
    return (
      <div className="space-y-6">
        <KpiLoadingState tiles={5} />
        <LoadingState rows={3} />
      </div>
    );
  if (error) return <ErrorState onRetry={() => refetch()} />;
  if (!data || (data.summary?.n_theses ?? 0) === 0)
    return <EmptyState title="No house views are recorded yet." />;

  const s = data.summary ?? {};
  const cal = data.calibration ?? {};
  const sla = data.conflict_sla ?? {};
  const asOf = data.as_of ? formatDay(data.as_of) : formatDay(new Date().toISOString());
  const hr = hitRatePct(cal.hit_rate);
  const mae = roundMiss(cal.mean_abs_error);
  // Only treat the track record as "scored" once at least one call has been evaluated — otherwise
  // hit_rate of 0 reads as a misleading "right 0% of the time".
  const scored = (cal.n_evaluated ?? 0) > 0;

  const asArray = (v: unknown): Row[] => (Array.isArray(v) ? (v as Row[]) : []);
  const reviewQueue = asArray(data.thesis_review_queue).slice(0, 5);
  const conflicts = asArray(data.open_conflicts).slice(0, 5);
  const gaps = asArray(data.coverage_gaps).slice(0, 5);
  const tensions = asArray(data.inconsistencies).slice(0, 5);

  return (
    <div className="space-y-8">
      <p className="font-heading text-xl">Good morning. Here's the house view as of {asOf}.</p>

      {/* KPI tiles */}
      <div className="flex flex-wrap gap-3">
        {s.n_theses != null && <KpiTile value={s.n_theses} caption="views we hold" />}
        {s.single_source_pct != null && (
          <KpiTile value={pct(s.single_source_pct)} caption="lean on a single source" secondary="worth corroborating" tone="attention" />
        )}
        {s.n_drift_flagged != null && (
          <KpiTile value={s.n_drift_flagged} caption="views the market has moved against" tone="attention" />
        )}
        {s.n_coverage_gaps != null && (
          <KpiTile value={s.n_coverage_gaps} caption="funds barely researched" tone={s.n_coverage_gaps > 0 ? "attention" : "neutral"} />
        )}
        {sla.n_open_formal != null && (
          <KpiTile
            value={sla.n_open_formal}
            caption="conflicts to settle"
            tone={sla.n_sla_breach ? "bad" : "neutral"}
            secondary={
              sla.n_sla_breach
                ? `${sla.n_sla_breach} overdue`
                : `none past the ${sla.sla_days ?? 14}-day deadline`
            }
          />
        )}
        {hr != null && scored && (
          <KpiTile
            value={`${hr}%`}
            caption={`right across ${cal.n_evaluated ?? 0} scored calls`}
            secondary={mae ? `typical miss about ${mae}` : undefined}
            tone="good"
          />
        )}
      </div>

      {/* Views under pressure */}
      <HubSection title="Views under pressure" seeAllTo={`${HUB}/today/views`}>
        {reviewQueue.length === 0 ? (
          <EmptyState title="Nothing needs attention here today." tone="good" />
        ) : (
          <div className="space-y-2">
            {reviewQueue.map((r: Row, i: number) => (
              <ThesisRow key={r.edge_id ?? i} r={r} onOpen={(id) => navigate(`${HUB}/thesis/${id}`)} />
            ))}
          </div>
        )}
      </HubSection>

      {/* Conflicts to settle */}
      <HubSection title="Conflicts to settle" seeAllTo={`${HUB}/today/conflicts`}>
        {conflicts.length === 0 ? (
          <EmptyState title="No open conflicts. Nicely settled." tone="good" />
        ) : (
          <div className="space-y-2">
            {conflicts.map((c: Row, i: number) => {
              const a = c.a_label ?? "A view";
              const b = c.b_label ?? "Another view";
              const ad = directionInfo(c.a_direction).glyph;
              const bd = directionInfo(c.b_direction).glyph;
              return (
                <Card key={c.conflict_id ?? i} className="p-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-body text-sm truncate">
                      {a} {ad} <span className="text-muted-foreground">vs</span> {b} {bd}
                    </div>
                    <div className="font-body text-xs text-muted-foreground">{conflictKind(c.kind)}</div>
                  </div>
                  <button
                    onClick={() => navigate(`${HUB}/today/conflicts`)}
                    className="font-body text-sm text-primary hover:underline shrink-0"
                  >
                    Review
                  </button>
                </Card>
              );
            })}
          </div>
        )}
      </HubSection>

      {/* Research blind spots */}
      <HubSection title="Research blind spots" seeAllTo={`${HUB}/today/coverage`}>
        {gaps.length === 0 ? (
          <EmptyState title="No notable blind spots — coverage is solid." tone="good" />
        ) : (
          <div className="space-y-2">
            {gaps.map((g: Row, i: number) => {
              const tier = supportTier(g.support_tier, g.no_source);
              return (
                <Card key={g.fund_id ?? i} className="p-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-body text-sm truncate">
                      {g.fund_label ?? "A fund"}{" "}
                      {g.portfolio != null && <span className="text-muted-foreground">(portfolio {g.portfolio})</span>}
                    </div>
                    <div className="font-body text-xs text-muted-foreground">
                      {g.weight_pct != null ? `${pct(g.weight_pct)} of the book` : "weight unknown"}
                    </div>
                  </div>
                  <AttentionBadge label={tier.label} tone={tier.tone} />
                </Card>
              );
            })}
          </div>
        )}
      </HubSection>

      {/* Tensions in the house view */}
      <HubSection title="Tensions in the house view" seeAllTo={`${HUB}/today/tensions`}>
        {tensions.length === 0 ? (
          <EmptyState title="Our views are holding together." tone="good" />
        ) : (
          <div className="space-y-2">
            {tensions.map((t: Row, i: number) => {
              const v = verdictLabel(t.verdict);
              return (
                <Card
                  key={t.edge_id ?? i}
                  className="p-3 flex items-center justify-between gap-3 cursor-pointer hover:bg-muted/50"
                  onClick={() => t.edge_id && navigate(`${HUB}/thesis/${t.edge_id}`)}
                >
                  <div className="font-body text-sm truncate min-w-0">
                    {beliefSentence(t.from_label, t.to_label, t.thesis_direction)}
                  </div>
                  <AttentionBadge label={v.label} tone={v.tone} />
                </Card>
              );
            })}
          </div>
        )}
      </HubSection>

      {/* Track record */}
      <HubSection title="Our track record" seeAllTo={`${HUB}/today/calibration`}>
        <Card className="p-5 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="font-heading text-2xl font-semibold text-primary">
              {hr != null && scored ? `Right about ${hr}% of the time` : "Not yet scored"}
            </div>
            <div className="font-body text-sm text-muted-foreground">
              {cal.n_evaluated ?? 0} of {cal.n_predictions ?? 0} calls scored
            </div>
          </div>
        </Card>
      </HubSection>
    </div>
  );
}

function ThesisRow({ r, onOpen }: { r: Row; onOpen: (id: string) => void }) {
  const sentence = beliefSentence(r.from_label ?? r.from, r.to_label ?? r.to, r.thesis_direction ?? r.direction);
  const hs = healthStatus(r.health_status);
  const staleness = r.staleness_days ?? r.days_since_evidence;
  return (
    <Card className="p-3 flex items-center justify-between gap-3">
      <div className="min-w-0 flex-1 cursor-pointer" onClick={() => r.edge_id && onOpen(r.edge_id)}>
        <div className="font-body text-sm truncate">{sentence}</div>
        <div className="flex items-center gap-3 mt-1 flex-wrap">
          <StrengthBar value={r.conviction_score} />
          {r.single_source_risk && <span className="font-body text-[11px] text-muted-foreground">one source</span>}
          {staleness != null && <span className="font-body text-[11px] text-muted-foreground">evidence {staleness} days old</span>}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <AttentionBadge label={hs.label} tone={hs.tone} />
        <AskAboutThis prefill={`Tell me more about our view that ${sentence}.`} label="" />
      </div>
    </Card>
  );
}
